// ADR-0034 §7.1.1 test #10 — Draft preview token sign/verify.
//
// HMAC-SHA256 signed token + DB row (post_preview_tokens_v2) lookup.
// Atomic consume = consumedAt update at verify time.
//
// Token format: `<postId>.<expiresAt-ms>.<hmac-base64url>`

import { createHmac, randomBytes, timingSafeEqual } from "node:crypto";

export interface PreviewTokenPayload {
  postId: string;
  expiresAt: Date;
}

const DELIMITER = ".";

function base64url(buf: Buffer): string {
  return buf.toString("base64url");
}

function fromBase64url(s: string): Buffer {
  return Buffer.from(s, "base64url");
}

function sign(secret: string, message: string): string {
  return base64url(createHmac("sha256", secret).update(message).digest());
}

/** 새 preview token 생성. DB insert 책임은 caller. */
export function issuePreviewToken(
  secret: string,
  payload: PreviewTokenPayload,
): string {
  const exp = payload.expiresAt.getTime();
  const nonce = base64url(randomBytes(12));
  const message = `${payload.postId}${DELIMITER}${exp}${DELIMITER}${nonce}`;
  const sig = sign(secret, message);
  return `${message}${DELIMITER}${sig}`;
}

/** Token 검증 (서명 + 만료). DB row consumed_at update 는 caller (atomic). */
export function verifyPreviewToken(
  secret: string,
  token: string,
): PreviewTokenPayload | null {
  const parts = token.split(DELIMITER);
  if (parts.length !== 4) return null;
  const [postId, expRaw, nonce, sig] = parts as [string, string, string, string];
  const exp = Number(expRaw);
  if (!Number.isFinite(exp)) return null;
  if (Date.now() > exp) return null;

  const expectedSig = sign(secret, `${postId}${DELIMITER}${exp}${DELIMITER}${nonce}`);
  const a = fromBase64url(sig);
  const b = fromBase64url(expectedSig);
  if (a.length !== b.length) return null;
  if (!timingSafeEqual(a, b)) return null;

  return { postId, expiresAt: new Date(exp) };
}
